import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ShellRail, useShellUI } from '../../shell/AppShell';
import MarketNav from './MarketNav';
import MarketLine from './MarketLine';
import MarketAvatar from './MarketAvatar';
import FillMoment from './FillMoment';
import ShareTicketModal from './ShareTicketModal';
import { pickOpenLine, pickCloseLine } from './fillCopy';
import {
  fetchPickAsset,
  placeOrder,
  cancelOrder,
  closePosition,
  watchPick,
  unwatchPick,
  fmtChips,
  fmtShares,
  shareWord,
  chipWord,
  fmtK,
  fmtPct,
  CHIPS_LABEL,
  CHART_RANGES,
  rangeCutoff,
  parseChipsInput,
  formatChipsInput,
} from './marketApi';
import './market.css';

const AMOUNTS = [5000, 10000, 25000];

const MarketPickAsset = () => {
  const { pickLabel } = useParams();
  const label = decodeURIComponent(pickLabel);
  const navigate = useNavigate();
  const { toast } = useShellUI();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [range, setRange] = useState('ALL');

  const [side, setSide] = useState('buy');
  const [amount, setAmount] = useState(10000);
  const [customAmount, setCustomAmount] = useState('');
  const [orderType, setOrderType] = useState('market');
  const [limitPrice, setLimitPrice] = useState('');
  const [ticketError, setTicketError] = useState(null);
  const [placing, setPlacing] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const [fillMoment, setFillMoment] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const fillNonceRef = useRef(0);
  const [tickClass, setTickClass] = useState('');
  const prevValueRef = useRef(null);

  useEffect(() => {
    setConfirming(false);
  }, [side, amount, customAmount, orderType, limitPrice]);

  const load = useCallback(() => {
    fetchPickAsset(label)
      .then(setData)
      .catch((e) => setError(e.message));
  }, [label]);

  useEffect(() => {
    setData(null);
    setError(null);
    load();
    const poll = setInterval(load, 60000);
    return () => clearInterval(poll);
  }, [load]);

  // Flash the hero number green/red when a poll actually moves the price.
  useEffect(() => {
    if (!data || !data.pick) return undefined;
    const val = data.pick.value;
    const prev = prevValueRef.current;
    prevValueRef.current = val;
    if (prev === null || val === null || val === undefined || val === prev) return undefined;
    setTickClass(val > prev ? 'mk-tick-up' : 'mk-tick-down');
    const t = setTimeout(() => setTickClass(''), 900);
    return () => clearTimeout(t);
  }, [data]);

  const showCloseFill = (r, sharesClosed) => {
    fillNonceRef.current += 1;
    setFillMoment({
      nonce: fillNonceRef.current,
      tone: r.position.pnl >= 0 ? 'profit' : 'loss',
      title: 'Closed.',
      heroTarget: r.position.pnlPct,
      heroFormat: (v) => `${v >= 0 ? '+' : ''}${v.toFixed(1)}%`,
      heroDecimals: 1,
      sub: `${fmtShares(sharesClosed)} sh · ${r.position.pnl >= 0 ? '+' : ''}${fmtChips(r.position.pnl)} ${chipWord(r.position.pnl)}`,
      line: pickCloseLine(r.position.pnl),
      positionId: r.position.id,
    });
  };

  const linePoints = useMemo(() => {
    if (!data) return [];
    const cutoff = rangeCutoff(range);
    if (!cutoff) return data.series;
    return data.series.filter((p) => p.date >= cutoff);
  }, [data, range]);

  if (error) {
    return (
      <div className="ft-view">
        <MarketNav />
        <p className="ft-sub">{error}</p>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="ft-view">
        <MarketNav />
        <div className="ft-loading">Pulling the tape</div>
      </div>
    );
  }

  const { pick, keyStats, myPosition, myOrders, watching, tradable, buyingPower } = data;
  const stake = customAmount ? parseInt(customAmount, 10) : amount;
  const priceBasis = orderType === 'limit' ? parseInt(limitPrice, 10) || null : pick.value;
  const estShares = stake && priceBasis ? stake / priceBasis : null;
  const overBuyingPower = Boolean(stake && buyingPower !== null && buyingPower !== undefined && stake > buyingPower);
  const first = linePoints[0];
  const last = data.series[data.series.length - 1];
  const rangeChangePct = first && last && first.value ? ((last.value - first.value) / first.value) * 100 : null;
  const duration = keyStats.matures ? Math.max(0, parseInt(keyStats.matures, 10) - new Date().getFullYear()) : null;

  const submitOrder = () => {
    setTicketError(null);
    setPlacing(true);
    placeOrder({
      pickLabel: label,
      action: side === 'sell' ? 'open_short' : 'open_long',
      type: orderType,
      amount: stake,
      limitPrice: orderType === 'limit' ? parseInt(limitPrice, 10) : undefined,
    })
      .then((r) => {
        if (r.position) {
          fillNonceRef.current += 1;
          setFillMoment({
            nonce: fillNonceRef.current,
            tone: 'you',
            title: 'Filled.',
            heroTarget: r.position.shares,
            heroFormat: (v) => `${fmtShares(v)} sh`,
            sub: `${fmtChips(r.position.amount)} ${chipWord(r.position.amount)}`,
            line: pickOpenLine(side === 'sell' ? 'short' : 'long'),
          });
        } else {
          toast('Resting until the market comes to it');
        }
        load();
      })
      .catch((e) => {
        setTicketError(
          e.message === 'guest_limit'
            ? 'Guest limit reached. Log in with your Sleeper username to keep trading.'
            : e.message
        );
      })
      .finally(() => setPlacing(false));
  };

  const act = (fn, okMsg) => () =>
    fn()
      .then(() => {
        toast(okMsg);
        load();
      })
      .catch((e) => toast(e.message));

  const stats = [
    { l: 'Matures', v: keyStats.matures },
    { l: 'Duration', v: duration !== null ? `${duration} ${duration === 1 ? 'year' : 'years'}` : null },
    { l: '30-day trend', v: keyStats.trend30Pct !== null ? fmtPct(keyStats.trend30Pct) : null },
    { l: 'Period high', v: keyStats.high52 ? fmtChips(keyStats.high52) : null },
    { l: 'Period low', v: keyStats.low52 ? fmtChips(keyStats.low52) : null },
    { l: 'Short interest', v: `${keyStats.shortInterest} ${keyStats.shortInterest === 1 ? 'desk' : 'desks'}` },
  ].filter((s) => s.v !== null && s.v !== undefined);

  return (
    <div className="ft-view">
      <Helmet>
        <title>{label} — The Market — Fantasy Taco</title>
      </Helmet>
      <MarketNav />

      <div className="mk-assethead">
        <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
          <MarketAvatar pick={pick} size={48} />
          <div>
          <p className="rh-label">
            {label}
            <span>draft pick · the bond desk</span>
          </p>
          <div className={`rh-bignum${tickClass ? ` ${tickClass}` : ''}`}>
            {pick.value ? fmtChips(pick.value) : '—'}
            <small>ktc</small>
          </div>
          <div className="rh-delta">
            {rangeChangePct !== null && (
              <span className={rangeChangePct >= 0 ? 'mk-up' : 'mk-dn'}>
                {rangeChangePct >= 0 ? '▲' : '▼'} {fmtPct(rangeChangePct)}
              </span>
            )}{' '}
            <span className="lbl">over {range === 'ALL' ? 'all time' : range}</span>
          </div>
          </div>
        </div>
        <button
          type="button"
          className={`mk-watch${watching ? ' on' : ''}`}
          aria-pressed={watching}
          onClick={act(() => (watching ? unwatchPick(label) : watchPick(label)), watching ? 'Off the watchlist' : 'Watching')}
        >
          {watching && <span className="dot" aria-hidden="true" />}
          {watching ? 'Watching' : 'Watch'}
        </button>
      </div>

      <div className="rh-chart">
        <MarketLine
          points={linePoints}
          color="var(--ft-ink)"
          fill="rgba(29,29,31,0.04)"
          ariaLabel={`${label} KTC value history`}
        />
      </div>
      <div className="rh-ranges" role="group" aria-label="Chart range">
        {CHART_RANGES.map((r) => (
          <button
            key={r.key}
            type="button"
            className={`rh-range${range === r.key ? ' on' : ''}`}
            onClick={() => setRange(r.key)}
          >
            {r.key}
          </button>
        ))}
      </div>

      {(myPosition || myOrders.length > 0) && (
        <div className="rh-statcards">
          {myPosition && (
            <div className="ft-card white">
              <p className="h">Your position</p>
              <div className="v">
                {fmtShares(myPosition.shares)}{' '}
                <span style={{ fontSize: 13, color: 'var(--ft-ink-3)', fontWeight: 600 }}>
                  {shareWord(myPosition.shares)}
                </span>
              </div>
              <div className="row">
                <span>Side</span>
                <span className="mk-mono">
                  {myPosition.side.toUpperCase()} · in {fmtK(myPosition.entryValue)}
                </span>
              </div>
              <div className="row">
                <span>Value</span>
                <span className="mk-mono">
                  {fmtChips(myPosition.marketValue)} {chipWord(myPosition.marketValue)}
                </span>
              </div>
              <div className="row">
                <span>Return</span>
                <span className={`mk-mono ${myPosition.pnl >= 0 ? 'mk-up' : 'mk-dn'}`}>
                  {myPosition.pnl >= 0 ? '+' : ''}
                  {fmtChips(myPosition.pnl)} ({fmtPct(myPosition.pnlPct)})
                </span>
              </div>
              <div className="row">
                <span />
                <button
                  type="button"
                  style={{ fontSize: 12.5, color: 'var(--ft-brand-ink)', fontWeight: 600 }}
                  onClick={() =>
                    closePosition(myPosition.id)
                      .then((r) => {
                        showCloseFill(r, myPosition.shares);
                        load();
                      })
                      .catch((e) => toast(e.message))
                  }
                >
                  {myPosition.side === 'short' ? 'Cover the short' : 'Sell all'}
                </button>
              </div>
            </div>
          )}
          {myOrders.map((o) => (
            <div className="ft-card white" key={o.id}>
              <p className="h">Your order</p>
              <div className="v">
                {o.amount ? fmtChips(o.amount) : '—'}{' '}
                <span style={{ fontSize: 13, color: 'var(--ft-ink-3)', fontWeight: 600 }}>
                  {o.action === 'close' ? 'close' : o.action === 'open_short' ? 'short' : 'buy'}
                  {o.type === 'limit' ? ' · limit' : ''}
                </span>
              </div>
              <div className="row">
                <span>Fills</span>
                <span className="mk-mono">
                  {o.type === 'limit' && o.limitPrice
                    ? `WHEN VALUE TOUCHES ${fmtK(o.limitPrice)}`
                    : 'TONIGHT AT THE CLOSE'}
                </span>
              </div>
              <div className="row">
                <span />
                <button
                  type="button"
                  style={{ fontSize: 12.5, color: 'var(--ft-ink-3)' }}
                  onClick={act(() => cancelOrder(o.id), 'Order cancelled')}
                >
                  Cancel
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="rh-sect">
        <h2>About</h2>
        <div className="rule" />
        <p className="rh-about">
          A claim on the {keyStats.matures || 'future'} rookie class
          {keyStats.round ? `, round ${keyStats.round}` : ''}. Picks trade like bonds — they mature
          at the draft, drift on class hype, and reward patience more than news. Nobody gets
          injured.
        </p>
      </div>

      <div className="rh-sect">
        <h2>Key statistics</h2>
        <div className="rule" />
        <div className="rh-kstats">
          {stats.map((s) => (
            <div className="rh-kstat" key={s.l}>
              <div className="l">{s.l}</div>
              <div className="v">{s.v}</div>
            </div>
          ))}
        </div>
      </div>

      {tradable && (
        <>
          <div className="mk-tradebar">
            <button
              type="button"
              className="ft-btn"
              onClick={() => document.querySelector('.ft-sheettoggle')?.click()}
            >
              Trade
            </button>
          </div>
          <div className="mk-tradebar-spacer" aria-hidden="true" />
        </>
      )}

      <ShellRail>
        <div className="ft-railsec">
          {tradable ? (
            <div className="rh-ticket">
              <div className="rh-tickhead">
                <button
                  type="button"
                  className={`rh-ticktab${side === 'buy' ? ' on' : ''}`}
                  aria-pressed={side === 'buy'}
                  onClick={() => {
                    setSide('buy');
                    setFillMoment(null);
                  }}
                >
                  Buy
                </button>
                <button
                  type="button"
                  className={`rh-ticktab${side === 'sell' ? ' on' : ''}`}
                  aria-pressed={side === 'sell'}
                  onClick={() => {
                    setSide('sell');
                    setFillMoment(null);
                  }}
                >
                  Short
                </button>
              </div>
              <div className="rh-tickbody">
                {fillMoment ? (
                  <FillMoment
                    nonce={fillMoment.nonce}
                    tone={fillMoment.tone}
                    title={fillMoment.title}
                    heroTarget={fillMoment.heroTarget}
                    heroFormat={fillMoment.heroFormat}
                    heroDecimals={fillMoment.heroDecimals || 0}
                    sub={fillMoment.sub}
                    line={fillMoment.line}
                    onShare={fillMoment.positionId ? () => setShareOpen(true) : undefined}
                    onDone={() => {
                      setFillMoment(null);
                      setShareOpen(false);
                    }}
                  />
                ) : (
                  <>
                {side === 'sell' && !myPosition && (
                  <p style={{ fontSize: 12.5, color: 'var(--ft-ink-2)', margin: '2px 0 8px', lineHeight: 1.45 }}>
                    You don't hold this pick, so this opens a short. You profit if it falls — sized by
                    buying power, not shares.
                  </p>
                )}
                <div className="rh-tickrow">
                  <span className="l">Order type</span>
                  <span className="v">
                    {orderType === 'market' ? 'Market' : 'Limit'}
                    <button
                      type="button"
                      onClick={() => setOrderType(orderType === 'market' ? 'limit' : 'market')}
                    >
                      {orderType === 'market' ? 'Switch to limit' : 'Switch to market'}
                    </button>
                  </span>
                </div>
                {orderType === 'limit' && (
                  <div className="rh-tickrow">
                    <span className="l">{side === 'buy' ? 'Buy at or below' : 'Short at or above'}</span>
                    <input
                      className="rh-custom"
                      style={{ maxWidth: 110, marginTop: 0, textAlign: 'right' }}
                      inputMode="numeric"
                      placeholder={pick.value ? String(pick.value) : 'value'}
                      value={limitPrice}
                      onChange={(e) => setLimitPrice(e.target.value.replace(/[^0-9]/g, ''))}
                      aria-label="Limit price"
                    />
                  </div>
                )}
                <div className="rh-tickrow">
                  <span className="l">Buying power</span>
                  <span className="v mk-mono">
                    {buyingPower !== null && buyingPower !== undefined ? fmtChips(buyingPower) : '—'}
                  </span>
                </div>
                <div className="rh-tickrow" style={{ display: 'block' }}>
                  <span className="l" style={{ display: 'block', marginBottom: 8 }}>
                    Amount
                  </span>
                  <div className="rh-amounts">
                    {AMOUNTS.map((a) => (
                      <button
                        key={a}
                        type="button"
                        className={`ft-fpill${!customAmount && amount === a ? ' on' : ''}`}
                        onClick={() => {
                          setAmount(a);
                          setCustomAmount('');
                        }}
                      >
                        {fmtChips(a)}
                      </button>
                    ))}
                    <button
                      type="button"
                      className={`ft-fpill${customAmount && Number(customAmount) === buyingPower ? ' on' : ''}`}
                      disabled={!buyingPower}
                      onClick={() => setCustomAmount(String(Math.floor(buyingPower)))}
                    >
                      All
                    </button>
                  </div>
                  <input
                    className="rh-custom"
                    inputMode="numeric"
                    placeholder={`Custom ${CHIPS_LABEL}`}
                    value={formatChipsInput(customAmount)}
                    onChange={(e) => setCustomAmount(parseChipsInput(e.target.value))}
                    aria-label="Custom amount"
                  />
                  {overBuyingPower && (
                    <p className="rh-tickerr" style={{ margin: 0 }}>
                      More than your buying power ({fmtChips(buyingPower)}).
                    </p>
                  )}
                </div>
                <div className="rh-tickrow" style={{ borderTop: '1px solid var(--ft-hairline)' }}>
                  <span className="l">Estimated shares</span>
                  <span className="v mk-mono">{estShares ? fmtShares(estShares) : '—'}</span>
                </div>
                <div className="rh-tickrow">
                  <span className="l">Fills</span>
                  <span className="v">{orderType === 'limit' ? 'The night it touches' : 'Now'}</span>
                </div>
                {ticketError && (
                  <div className="rh-tickerr">
                    {ticketError}{' '}
                    {ticketError.startsWith('Guest limit') && (
                      <button
                        type="button"
                        style={{ color: 'var(--ft-brand-ink)', fontWeight: 600 }}
                        onClick={() => navigate('/login')}
                      >
                        Log in
                      </button>
                    )}
                  </div>
                )}
                <button
                  type="button"
                  className="ft-btn"
                  style={{ width: '100%', justifyContent: 'center', marginTop: 10 }}
                  disabled={placing || !stake || overBuyingPower || (orderType === 'limit' && !limitPrice)}
                  onClick={() => {
                    if (!confirming) {
                      setConfirming(true);
                      return;
                    }
                    setConfirming(false);
                    submitOrder();
                  }}
                >
                  {placing
                    ? 'Sending'
                    : confirming
                      ? `Confirm — ${side === 'buy' ? 'buy' : 'short'} ${estShares ? `${fmtShares(estShares)} ${shareWord(estShares)}` : 'shares'}`
                      : orderType === 'limit'
                        ? 'Let it rest'
                        : side === 'buy'
                          ? 'Buy now'
                          : 'Short now'}
                </button>
                {confirming && !placing && (
                  <button
                    type="button"
                    style={{
                      width: '100%',
                      textAlign: 'center',
                      marginTop: 8,
                      fontSize: 12.5,
                      color: 'var(--ft-ink-3)',
                      padding: 6,
                    }}
                    onClick={() => setConfirming(false)}
                  >
                    Never mind
                  </button>
                )}
                  </>
                )}
              </div>
            </div>
          ) : (
            <div className="ft-card" style={{ padding: '16px 18px' }}>
              <p style={{ fontSize: 13.5, color: 'var(--ft-ink-2)', margin: 0 }}>
                No market on this pick right now.
              </p>
            </div>
          )}
        </div>
        <div className="ft-railsec">
          <span className="ft-k">Why picks</span>
          <p style={{ fontSize: 13, color: 'var(--ft-ink-2)', margin: 0, lineHeight: 1.5 }}>
            The most predictable curves in dynasty — rise into draft season, sag after. Long
            duration pays if the class is real.
          </p>
        </div>
      </ShellRail>

      <ShareTicketModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        positionId={fillMoment?.positionId}
        assetName={label}
        assetPath={`/market/pick/${encodeURIComponent(label)}`}
        pnlPct={fillMoment?.heroTarget ?? 0}
      />
    </div>
  );
};

export default MarketPickAsset;
